import React, { useEffect, useState } from 'react';
import { Modal } from "react-bootstrap";
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { storeActivityIndicator } from '../../../../redux/reducers/authSlice';
import { durumRengi, MutabakatDetayi, tl, YurticiMutabakatService } from '../../../../services/YurticiMutabakatService';

interface Props {
    satirId: string | undefined
    onClose: () => void
}

const SAYI: React.CSSProperties = { textAlign: 'right', whiteSpace: 'nowrap' };

/** Gönderi ayrıntısı: rapor tutarları, siparişteki beklenen gider satırları, web servis tutarı, geçmiş ve inceleme notu. */
const DetayModal = ({ satirId, onClose }: Props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [detay, setDetay] = useState<MutabakatDetayi | undefined>(undefined);

    useEffect(() => {
        setDetay(undefined);

        if (satirId)
            getir();
    }, [satirId])

    const getir = async () => {
        try {
            dispatch(storeActivityIndicator(true));

            var response = await YurticiMutabakatService.detay(satirId!);

            if (!response.isSuccess) {
                toast.error(response.message ?? '');
                return;
            }

            setDetay(response.data);
        } catch (error: any) {
            toast.error(error);
        } finally {
            dispatch(storeActivityIndicator(false));
        }
    }

    const s = detay?.satir;
    const bilgi = (etiket: string, deger: React.ReactNode) => (
        <div className="col-6 col-md-3 mb-3">
            <div className="text-muted fs-8">{etiket}</div>
            <div className="fw-bold">{deger ?? '—'}</div>
        </div>
    );

    return (
        <Modal show={!!satirId} onHide={onClose} size="xl" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    Gönderi {s?.gonderiKodu}
                    {s && <span className={`badge badge-light-${durumRengi(s.durum)} ms-3 fs-7`}>{s.durumAdi}</span>}
                    {s?.incelendi && <span className="badge badge-light-success ms-2 fs-7">İncelendi</span>}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {
                    detay && s && (
                        <React.Fragment>
                            <div className="row">
                                {bilgi('Sipariş', s.yurticiCargoId
                                    ? <a className="cursor-pointer" onClick={() => navigate('/yurticicargo/listadd?id=' + s.yurticiCargoId)}>{s.siparisRefNo}</a>
                                    : null)}
                                {bilgi('Müşteri', s.musteriAdi)}
                                {bilgi('Gönderi Tarihi', s.gonderiTarihiStr)}
                                {bilgi('Fark (Yurtiçi − Keylogx)', s.fark == null ? null : <span className={s.fark > 0 ? 'text-danger' : s.fark < 0 ? 'text-success' : ''}>{tl(s.fark)}</span>)}
                            </div>

                            <h6 className="mt-2">Yurtiçi fatura raporu</h6>
                            <div className="row">
                                {bilgi('Fatura No', s.faturaNo)}
                                {bilgi('Fatura Tarihi', s.faturaTarihiStr)}
                                {bilgi('Fatura Tipi', s.faturaTipi)}
                                {bilgi('Kargo Statüsü', detay.kargoStatusu)}
                                {bilgi('Ürün Bedeli', detay.urunBedeli == null ? null : tl(detay.urunBedeli))}
                                {bilgi('İrsaliye Matrahı', detay.matrah == null ? null : tl(detay.matrah))}
                                {bilgi('KDV', detay.kdv == null ? null : tl(detay.kdv))}
                                {bilgi('İrsaliye Matrahı + KDV', detay.kdvDahil == null ? null : tl(detay.kdvDahil))}
                                {bilgi('Desi / Kg', detay.desiKg?.toLocaleString('tr-TR'))}
                                {bilgi('Kargo Adedi', detay.kargoAdedi)}
                                {bilgi('Müşteri Kodu', detay.musteriKodu)}
                                {bilgi('Web Servis Tutarı', s.webServisTutari == null ? null : tl(s.webServisTutari) + (detay.webServisZamani ? ' (' + detay.webServisZamani + ')' : ''))}
                            </div>

                            <h6 className="mt-2">Siparişteki beklenen gider satırları</h6>
                            <table className="table table-row-dashed fs-7 mb-4">
                                <thead>
                                    <tr className="fw-bolder text-muted">
                                        <th>Hizmet</th>
                                        <th style={SAYI}>KDV Hariç</th>
                                        <th style={SAYI}>KDV</th>
                                        <th style={SAYI}>KDV Dahil</th>
                                        <th>Fatura No</th>
                                        <th>Açıklama</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detay.giderSatirlari.length == 0 && <tr><td colSpan={6} className="text-center text-muted">Beklenen gider satırı yok.</td></tr>}
                                    {
                                        detay.giderSatirlari.map((g, i) => (
                                            <tr key={i}>
                                                <td>{g.hizmetAdi}</td>
                                                <td style={SAYI}>{tl(g.kdvHaric)}</td>
                                                <td style={SAYI}>{tl(g.kdv)}</td>
                                                <td style={SAYI}>{tl(g.kdvDahil)}</td>
                                                <td>{g.faturaNo}</td>
                                                <td>{g.aciklama}</td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                            <div className="text-muted fs-8 mb-4">Karşılaştırmada yalnız "YURTİÇİ KARGO BEDELİ" satırlarının KDV hariç toplamı kullanılır.</div>

                            {
                                s.incelendi && (
                                    <div className="alert alert-light-success py-2 mb-4">
                                        <b>İncelendi</b> — {detay.incelemeYapan} · {detay.incelemeZamani}<br />{s.incelemeNotu}
                                    </div>
                                )
                            }

                            <h6>Geçmiş</h6>
                            <table className="table table-row-dashed fs-7">
                                <thead>
                                    <tr className="fw-bolder text-muted">
                                        <th>Zaman</th>
                                        <th>Kaynak</th>
                                        <th style={SAYI}>Tutar</th>
                                        <th>Fatura No</th>
                                        <th>Açıklama</th>
                                        <th>Kullanıcı</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detay.gecmis.length == 0 && <tr><td colSpan={6} className="text-center text-muted">Kayıt yok.</td></tr>}
                                    {
                                        detay.gecmis.map((g, i) => (
                                            <tr key={i}>
                                                <td style={{ whiteSpace: 'nowrap' }}>{g.zaman}</td>
                                                <td>{g.kaynakAdi}</td>
                                                <td style={SAYI}>{g.tutar == null ? '' : tl(g.tutar)}</td>
                                                <td>{g.faturaNo}</td>
                                                <td>{g.aciklama}</td>
                                                <td>{g.kullanici}</td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </table>
                            {detay.karsilastirmaZamani && <div className="text-muted fs-8">Son karşılaştırma: {detay.karsilastirmaZamani}</div>}
                        </React.Fragment>
                    )
                }
            </Modal.Body>
        </Modal>
    )
}

export default DetayModal;
